import { GripVertical } from '@tamagui/lucide-icons';
import { AnimatedImage } from '@unistocks/components/basic/image';
import {
  DraggableItemItemContainer,
  useDraggableItemStyle
} from '@unistocks/components/sortables/draggable-item';
import { SwitchButton } from '@unistocks/components/switch/switch-button';
import { Paragraph } from '@unistocks/components/text/text';
import { useAssetImage } from '@unistocks/features/assets/hooks/use-asset-image';
import {
  SectionListItem,
  SortablePortfolioItem
} from '@unistocks/features/portfolios/components/portfolio-settings/sort/types';
import { SectionAssetDto } from '@unistocks/proxy/axios-client';
import {
  FadeInLeftTransition,
  FadeOutLeftTransition,
  quicker,
  QuickerTransition
} from '@unistocks/shared/animations';
import { t } from '@unistocks/shared/localization/index.ts';
import { isDefined } from '@unistocks/shared/utils/util';
import { PropsWithChildren } from 'react';
import Animated, { useAnimatedStyle, withSpring } from 'react-native-reanimated';
import Sortable, { CustomHandleProps, useItemContext } from 'react-native-sortables';
import { getTokenValue, useTheme } from 'tamagui';

const AnimatedText = Animated.createAnimatedComponent(Paragraph);

type DraggableItemProps = PropsWithChildren<{
  item: SortablePortfolioItem['item'];
  mode: CustomHandleProps['mode'];
}>;

const DraggableItem: React.FC<DraggableItemProps> = ({ item, mode, children }) => {
  const { isActive } = useItemContext();
  const isAsset = isDefined((item as SectionAssetDto).type);
  // Will use default image for sections
  const imageUri = useAssetImage(item as SectionAssetDto);
  const isDraggable = mode === 'draggable';

  const space2 = getTokenValue('$2', 'space');
  const space3 = getTokenValue('$3', 'space');
  const space5 = getTokenValue('$5', 'space');

  const draggableItemStyle = useDraggableItemStyle();

  const handleStyle = useAnimatedStyle(() => {
    return {
      display: isActive.value ? 'none' : 'flex'
    };
  });

  const imageStyle = useAnimatedStyle(() => {
    const marginLeft = isActive.value ? space3 : isDraggable ? space2 : isAsset ? space5 : 0;

    return {
      marginLeft: withSpring(marginLeft, quicker)
    };
  });

  return (
    <Sortable.Handle mode={mode}>
      <DraggableItemItemContainer
        pl={0}
        cursor={isDraggable ? 'grab' : undefined}
        style={draggableItemStyle}
      >
        {isDraggable ? (
          <Animated.View
            entering={FadeInLeftTransition}
            exiting={FadeOutLeftTransition}
            style={handleStyle}
          >
            <GripVertical color={'$unistocks'} />
          </Animated.View>
        ) : null}
        <AnimatedImage
          rounded
          size={'$3'}
          mx={'$2'}
          source={imageUri}
          layout={QuickerTransition}
          style={imageStyle}
        />
        {children}
      </DraggableItemItemContainer>
    </Sortable.Handle>
  );
};

export const DraggableAsset: React.FC<{ asset: SectionAssetDto }> = ({ asset }) => {
  return (
    <DraggableItem item={asset} mode={'draggable'}>
      <AnimatedText ellipsis size={'$6'} layout={QuickerTransition}>
        {asset.name}
      </AnimatedText>
    </DraggableItem>
  );
};

export const DraggableSection: React.FC<{
  section: SectionListItem;
  index: number;
  isDraggable: boolean;
  toggleAutoSort: () => void;
}> = ({ section, index, isDraggable, toggleAutoSort }) => {
  const { unistocks, color5, gray4, gray9 } = useTheme();
  const isAutoSorted = section.sorting.property !== null;
  const mode: CustomHandleProps['mode'] = isDraggable
    ? 'draggable'
    : index === 0
      ? 'fixed-order'
      : 'non-draggable';

  return (
    <DraggableItem item={section} mode={mode}>
      <AnimatedText size={'$8'} layout={QuickerTransition}>
        {section.name}
      </AnimatedText>
      {!isDraggable ? (
        <SwitchButton
          ml={'auto'}
          checked={isAutoSorted}
          onCheckedChange={toggleAutoSort}
          rightLabel={t('::Auto')}
          leftLabel={t('::Manual')}
          nativeProps={{
            thumbColor: isAutoSorted ? unistocks.get() : gray9.get(),
            trackColor: {
              false: gray4.get(),
              true: color5.get()
            }
          }}
        />
      ) : null}
    </DraggableItem>
  );
};
